import React from 'react';
import { FormattedMessage, injectIntl, defineMessages } from 'react-intl';
import PropTypes from 'prop-types';
import _pick from 'lodash/pick';
import _get from 'lodash/get';
import hash from 'hash.js';
import ComposeMessaging from '../ComposeMessaging';
import Segment from '../../components/Segment';
import { MessagingOptions, MessagingErrors } from '../ComposeMessaging/ComposeMessaging';
import Toast from '../../components/Toast';
import Header from '../../components/Header';
import { sortByName, audienceToString } from './utils';
import styles from './styles.css';

const messages = defineMessages({
  saveSuccess: {
    id: 'amnext.messages.competitors.message.success',
    defaultMessage: 'Competitor messages have been successfully updated.',
  },
  shouldCheckMessages: {
    id: 'amnext.messages.competitors.message.check',
    defaultMessage: 'Please, Your message has been formatted. Please, check them again.',
  },
  unknownError: {
    id: 'amnext.messages.competitors.error.unknown',
    defaultMessage: 'Something is wrong in your data',
  },
  mainTitle: {
    id: 'amnext.messages.competitors.title',
    defaultMessage: 'Competitors',
  },
  firstPar: {
    id: 'amnext.messages.competitors.par.first',
    defaultMessage: 'Compose messages for students interested in your selected competitors.',
  },
  optionNoMessage: {
    id: 'amnext.messages.competitors.option.nomessage',
    defaultMessage: 'Display without message',
  },
  optionSameForAll: {
    id: 'amnext.messages.competitors.option.sameforall',
    defaultMessage: 'Show same message to all audiences',
  },
  optionDifferentiate: {
    id: 'amnext.messages.competitors.option.differentiate',
    defaultMessage: 'Differentiate message per audience',
  },
});

function formatMesssages(data) {
  const { competitors } = data;
  const subCategories = {};
  const { NODE_ENV } = process.env;

  const categories = competitors && competitors.map((me) => {
    const { name, collegeCore, scid, messageOption, defaultMessage, messages: messageList } = me;
    const city = _get(collegeCore, 'address.city');
    const state = _get(collegeCore, 'address.state');
    subCategories[scid] = messageList ?
      messageList.map((subMessage) => {
        const { audience, message: competitorMessage } = subMessage;

        const id = hash.sha1().update(
          (NODE_ENV !== 'test' ? Date.now() : '') + JSON.stringify(subMessage),
        ).digest('hex');
        return {
          id,
          audience,
          name: audienceToString(audience),
          message: competitorMessage,
        };
      }).sort(sortByName): [];

    const location = (city && state) ? ` (${city}, ${state})`: '';
    const newCategory = {
      type: messageOption || MessagingOptions.NOMESSAGE,
      defaultMessage,
      id: scid,
      name,
      extraName: <span className={styles.normalText}>{location}</span>,
      messages: subCategories[scid].map((sc) => ({ refId: sc.id, message: sc.message })),
    };

    if (messageOption === MessagingOptions.SAMEFORALL) {
      newCategory.messaging = newCategory.defaultMessage;
    } else if (messageOption === MessagingOptions.DIFFERENTIATE) {
      newCategory.messaging = newCategory.messages;
    }

    return newCategory;
  });

  return {
    categories: categories.sort(sortByName),
    subCategories,
  };
}

class CompetitorsMessagingPage extends React.PureComponent {
  static propTypes = {
    intl: PropTypes.shape({
      formatMessage: PropTypes.func.isRequired,
    }).isRequired,
    updateCompetitorsMessaging: PropTypes.func.isRequired,
    messages: PropTypes.object,
  };

  static defaultProps = {
    messages: [],
  };

  static getDerivedStateFromProps(props, state) {
    const { messages: messageList } = props;
    const newState = {};

    if (state.prevMessages !== messageList) {
      const formatted = formatMesssages(messageList);
      newState.prevMessages = messageList;
      newState.subCategories = formatted.subCategories;
      newState.categories = formatted.categories;
    }
    return newState;
  }

  state = {
    showSuccess: null,
    showFailure: null,
    prevMessages: null,
    categories: [],
    subCategories: {},
  };

  handleSuccessToastDismiss = () => {
    this.setState({
      showSuccess: null,
    });
  }

  handleFailureToastDismiss = () => {
    this.setState({
      showFailure: null,
    });
  }

  handleSubmit = (data) => {
    const { intl } = this.props;
    const { subCategories } = this.state;

    const sendable = data.reduce((acc, cat) => {
      const { id, type, defaultMessage, messages: messageList, messaging } = cat;
      const newCat = {
        scid: id,
        messageOption: type,
        defaultMessage,
        messages: messageList,
      };

      if (type === MessagingOptions.SAMEFORALL) {
        newCat.defaultMessage = messaging;
      } else if (type === MessagingOptions.DIFFERENTIATE) {
        newCat.messages = messaging;
      }
      newCat.messages = Array.isArray(messaging) ? messaging.map((mc) => {
        const competitorMessage = subCategories[id.toString()].find((co) => co.id === mc.refId);
        const { zipCode, state, county, sector, diversity } = competitorMessage.audience;

        const cleanedAudience = {
          state,
          sector,
          diversity,
        };
        cleanedAudience.county = county ? {
          ..._pick(county, ['state', 'county']),
        }: null;

        cleanedAudience.zipCode = zipCode ? {
          ..._pick(zipCode, ['zipCode', 'radius']),
        }: null;

        return {
          message: mc.message,
          audience: cleanedAudience,
        };
      }) : [];

      acc.push(newCat);
      return acc;
    }, []);
    return this.props.updateCompetitorsMessaging({
      variables: {
        input: {
          version: this.props.messages.version,
          competitors: sendable,
        },
      },
    }).then(() => {
      this.setState({
        showSuccess: intl.formatMessage(messages.saveSuccess),
      });
      return Promise.resolve();
    })
      .catch((error) => {
        this.setState({
          showFailure: error.message,
        });
        /*
         * This rejection is to let UnsavedChangesPrompt that should not change the page
         * The value is not used for any purpose
         */
        return Promise.reject(error);
      });
  }

  handleError = ({ type }) => {
    const { intl } = this.props;
    if (type === MessagingErrors.CHECK_MESSAGES) {
      this.setState({
        showFailure: intl.formatMessage(messages.shouldCheckMessages),
      });
    } else {
      this.setState({
        showFailure: intl.formatMessage(messages.unknownError),
      });
    }
  }

  render() {
    const { intl } = this.props;
    const { categories, subCategories, showSuccess, showFailure } = this.state;
    const competitorsOptions = {
      [MessagingOptions.NOMESSAGE]: intl.formatMessage(messages.optionNoMessage),
      [MessagingOptions.SAMEFORALL]: intl.formatMessage(messages.optionSameForAll),
      [MessagingOptions.DIFFERENTIATE]: intl.formatMessage(messages.optionDifferentiate),
    };

    return (
      <Segment>
        <Toast
          active={!!showSuccess}
          title=""
          kind="success"
          description={showSuccess}
          onDeactivate={this.handleSuccessToastDismiss}
        />
        <Toast
          active={!!showFailure}
          title=""
          kind="error"
          description={showFailure}
          onDeactivate={this.handleFailureToastDismiss}
        />
        <Header as="h2" size="medium">{ intl.formatMessage(messages.mainTitle) }</Header>
        <p><FormattedMessage {...messages.firstPar} /></p>
        <ComposeMessaging
          options={competitorsOptions}
          list={categories}
          refsPerEntry
          refList={subCategories}
          onSubmit={this.handleSubmit}
          onError={this.handleError}
        />
      </Segment>
    );
  }
}

export default injectIntl(CompetitorsMessagingPage);
